import type { HttpClient } from '../client/http';

/**
 * Reminders SDK module — CRUD sobre slots de reminder + agendamento ad-hoc +
 * fallbacks por slot. Usado via `push.reminders` no retorno de `useHook()`.
 *
 * Endpoints (todos auth-required, escopados por appId):
 *   - GET    /api/app/:appId/reminders              → list
 *   - PUT    /api/app/:appId/reminders              → set (upsert por slot)
 *   - DELETE /api/app/:appId/reminders/:slot        → delete
 *   - POST   /api/app/:appId/reminders/schedule     → agenda items ad-hoc
 *   - PUT    /api/app/:appId/reminders/fallbacks    → define fallbacks por slot
 */

export interface ReminderSlot {
  id?: string;
  slot: string;
  timeLocal: string;
  timezone: string;
  enabled: boolean;
  lastFiredAt?: string | null;
}

export interface RemindersMethods {
  /** Lista reminders ativos do usuário corrente nesse app. */
  list(): Promise<ReminderSlot[]>;
  /** Upsert por `slot` — cria ou atualiza horário/timezone/enabled. */
  set(input: {
    slot: string;
    timeLocal: string;
    timezone: string;
    enabled: boolean;
  }): Promise<void>;
  /** Remove reminder do slot. Idempotente no backend. */
  delete(slot: string): Promise<void>;
  /**
   * Agenda batch de reminders ad-hoc (ex.: lembretes gerados a partir
   * de evento do dia). `sendAt` aceita Date ou ISO string — normalizado
   * pra ISO antes do POST.
   *
   * Audit Wave 3 — Fix #55: `url` é required. SW notificationclick handler
   * abre `data.url`; sem url o click cai pra '/' e perde contexto da
   * notificação. Backend rejeita batch sem url com 400.
   */
  schedule(items: Array<{
    slot: string;
    sendAt: string | Date;
    title: string;
    body: string;
    url: string;
  }>): Promise<{ accepted: number; rejected: number }>;
  /**
   * Define fallback (título/corpo/url) por slot pra quando o agendador disparar.
   * Audit Wave 3 — Fix #55: `url` é required (mesma razão de `schedule`).
   */
  setFallbacks(items: Array<{
    slot: string;
    title: string;
    body: string;
    url: string;
  }>): Promise<void>;
}

export function createReminders(client: HttpClient, appId: string): RemindersMethods {
  const base = `/api/app/${appId}/reminders`;
  return {
    async list() {
      return client.request<ReminderSlot[]>('GET', base);
    },
    async set(input) {
      await client.request('PUT', base, { body: input });
    },
    async delete(slot) {
      await client.request('DELETE', `${base}/${encodeURIComponent(slot)}`);
    },
    async schedule(items) {
      const normalized = items.map((i) => ({
        slot: i.slot,
        sendAt: i.sendAt instanceof Date ? i.sendAt.toISOString() : i.sendAt,
        title: i.title,
        body: i.body,
        url: i.url,
      }));
      return client.request<{ accepted: number; rejected: number }>(
        'POST',
        `${base}/schedule`,
        { body: normalized },
      );
    },
    async setFallbacks(items) {
      await client.request('PUT', `${base}/fallbacks`, { body: items });
    },
  };
}
